import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const MAX_RANGE_DAYS = 31;

const DATE_FORMAT = 'YYYY-MM-DD';

export interface DateRange {
  from: string;
  to: string;
}

export type RangeValidation =
  | { valid: true }
  | { valid: false; reason: 'invalid' | 'order' | 'too_long' };

/** Las fechas `AAAA-MM-DD` se operan en UTC para que ningún huso las corra. */
const parse = (date: string) => dayjs.utc(date, DATE_FORMAT, true);

/**
 * Semana lunes-domingo que contiene AYER (D-1) en el huso del usuario, con el
 * fin recortado a ayer: el día en curso nunca está cerrado. Un lunes devuelve
 * la semana anterior completa; un martes, solo el lunes.
 */
export const getDefaultRange = (now: Dayjs, tz: string): DateRange => {
  const yesterday = parse(now.tz(tz).format(DATE_FORMAT)).subtract(1, 'day');
  const daysSinceMonday = (yesterday.day() + 6) % 7;

  return {
    from: yesterday.subtract(daysSinceMonday, 'day').format(DATE_FORMAT),
    to: yesterday.format(DATE_FORMAT),
  };
};

/** Días del rango, ambos extremos incluidos. */
const spanDays = (range: DateRange): number =>
  parse(range.to).diff(parse(range.from), 'day') + 1;

export const validateRange = (range: DateRange): RangeValidation => {
  if (!parse(range.from).isValid() || !parse(range.to).isValid()) {
    return { valid: false, reason: 'invalid' };
  }
  if (parse(range.to).isBefore(parse(range.from))) {
    return { valid: false, reason: 'order' };
  }
  if (spanDays(range) > MAX_RANGE_DAYS) {
    return { valid: false, reason: 'too_long' };
  }

  return { valid: true };
};

/**
 * Corrige un rango: fechas invalidas caen al rango por defecto, fin < inicio
 * se iguala al inicio y el largo se topa a 31.
 */
export const clampRange = (
  range: DateRange,
  now: Dayjs,
  tz: string
): DateRange => {
  const from = parse(range.from);
  if (!from.isValid() || !parse(range.to).isValid()) {
    return getDefaultRange(now, tz);
  }
  if (parse(range.to).isBefore(from)) {
    return { from: range.from, to: range.from };
  }
  if (spanDays(range) > MAX_RANGE_DAYS) {
    return {
      from: range.from,
      to: from.add(MAX_RANGE_DAYS - 1, 'day').format(DATE_FORMAT),
    };
  }

  return range;
};

const MAX_ENUMERATED_DAYS = 366;

/**
 * Todas las fechas del rango, tambien las que no tienen rutas. Entrada
 * invalida o invertida devuelve []; el recorrido tiene un tope duro.
 */
export const enumerateDays = (from: string, to: string): string[] => {
  const start = parse(from);
  const end = parse(to);
  if (!start.isValid() || !end.isValid()) return [];

  const days: string[] = [];
  for (
    let d = start;
    !d.isAfter(end) && days.length < MAX_ENUMERATED_DAYS;
    d = d.add(1, 'day')
  ) {
    days.push(d.format(DATE_FORMAT));
  }

  return days;
};
